import { getWebPath } from '@doss/common-all'
import { all as promiseAll } from 'bluebird'
import { useFormikContext } from 'formik'
import { isEmpty, isNil } from 'lodash'
import { MouseEvent, useEffect, useState } from 'react'
import { FiEdit3 } from 'react-icons/fi'
import { RxPlus } from 'react-icons/rx'
import { InputWrapper } from '~/@/components/inputs/base/input-wrapper'
import { BaseInputWrapperProps } from '~/@/components/inputs/base/types'
import { FileChip } from '~/@/components/ui/file-chip'
import {
  BreadcrumbObjectType,
  useCreateFileCellDocumentViewMutation,
  useDeleteDocumentMutation,
  useGetFileCellDocumentLazyQuery,
} from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { getDropzoneAcceptOptionForAllDocumentTypes } from '~/common/utils/document'
import { DropzoneDialog } from '~/components/dropzone/DropzoneDialog'
import { FileInlineImage } from '~/components/table-cells/FileTableCells/FileInlineImage'
import { useDossSnackbar } from '../../shared/DossSnackbar'
import { SignatureChip } from '../../ui/molecules/file-chip/SignatureChip'
import { AddRecordFormCell, RecordDetailTableCell } from '../types'

interface FileCellDetailsProps {
  cell: RecordDetailTableCell | AddRecordFormCell
  documentId: string | null
  addDocumentId?: (documentId: string) => void
  removeDocumentId?: (documentId: string) => void
  inputWrapperProps?: BaseInputWrapperProps
}

/**
 * Renders a file cell's details
 */
export function FileCellDetails({
  cell,
  documentId,
  addDocumentId,
  removeDocumentId,
  inputWrapperProps,
}: FileCellDetailsProps) {
  const { t } = useTypedTranslation()
  const formik = useFormikContext<Record<string, string | null>>()
  const fieldName = cell.column.id
  const snackbar = useDossSnackbar()
  const [isUploading, setIsUploading] = useState(false)
  const [getFile, { data: fileCellDocumentData }] = useGetFileCellDocumentLazyQuery()
  const [createFileCellDocumentView] = useCreateFileCellDocumentViewMutation({
    update: (cache, result) => {
      if (result.data?.createDocumentView.id) {
        formik.setFieldTouched(fieldName, true)
        formik.setFieldValue(fieldName, result.data.createDocumentView.id, true)
      }
    },
    onCompleted({ createDocumentView }) {
      if (addDocumentId) {
        addDocumentId(createDocumentView.id)
      }
    },
  })
  const [deleteDocument] = useDeleteDocumentMutation({
    update(cache, { data }) {
      if (!data) {
        return
      }

      const normalizedId = cache.identify(data.deleteDocument)
      cache.evict({ id: normalizedId })
      cache.gc()
    },
  })

  const [showDropzone, setShowDropzone] = useState(false)
  const toggleDropzone = () => setShowDropzone((isVisible) => !isVisible)

  let isDirty = false
  if (!isEmpty(cell.documents)) {
    // If the cell isn't denormalized yet, there will be documents but the cell.data will still be
    // null. In that case, it isn't dirty.
    const isPreloadedDocument = isNil(cell.data) && isNil(formik.values[fieldName])
    isDirty = cell.documents[0].id !== formik.values[fieldName] && !isPreloadedDocument
  } else {
    isDirty = formik.values[fieldName] !== null
  }

  const fileCellDocumentId = isDirty ? formik.values[fieldName] : documentId

  useEffect(() => {
    if (fileCellDocumentId) {
      getFile({ variables: { documentId: fileCellDocumentId } })
    }
  }, [getFile, fileCellDocumentId, fileCellDocumentData])

  const handleFileDelete = async (event?: MouseEvent<HTMLButtonElement, Event>) => {
    if (event) {
      event.preventDefault()
    }
    // If file is not the original file on the cell, delete it
    if (fileCellDocumentId && fileCellDocumentId !== documentId) {
      await deleteDocument({ variables: { documentId: fileCellDocumentId } })
      if (removeDocumentId) {
        removeDocumentId(fileCellDocumentId)
      }
    }
    formik.setFieldValue(fieldName, null)
    formik.setFieldTouched(fieldName, true)
  }

  const displayImageInline = cell.column.displayImagesInline

  const hasFileData = fileCellDocumentId && fileCellDocumentData
  let fileDisplay = null

  // Field should be disabled if the column is read only and in edit mode
  const isReadOnly = cell.column.isReadOnly && 'id' in cell

  if (hasFileData) {
    if (fileCellDocumentData.document.signature) {
      fileDisplay = (
        <SignatureChip
          document={fileCellDocumentData.document}
          signature={fileCellDocumentData.document.signature}
        />
      )
    } else if (displayImageInline) {
      fileDisplay = (
        <FileInlineImage
          disabled={isReadOnly}
          document={fileCellDocumentData.document}
          onDelete={isReadOnly ? undefined : (event) => handleFileDelete(event)}
        />
      )
    } else {
      const fileUrl = getWebPath({
        objectId: fileCellDocumentData.document.id,
        objectType: BreadcrumbObjectType.DOCUMENT,
      })

      fileDisplay = (
        <FileChip
          disabled={isReadOnly}
          document={fileCellDocumentData.document}
          fileName={fileCellDocumentData.document.name}
          fileUrl={fileUrl}
          onDelete={(event) => {
            event.preventDefault()
            event.stopPropagation()
            handleFileDelete(event)
          }}
        />
      )
    }
  }

  return (
    <InputWrapper {...inputWrapperProps}>
      <div className="flex flex-wrap gap-2">
        {fileDisplay}
        {!isReadOnly && (
          <button
            className="flex min-h-6 items-center gap-1.5 ps-1 text-xs-tall font-normal text-gray-10 hover:text-blue-9"
            onClick={(e) => {
              e.preventDefault()
              toggleDropzone()
            }}
            tabIndex={0}
            type="button"
          >
            {fileCellDocumentId ? <FiEdit3 /> : <RxPlus />}
            {fileCellDocumentId ? t('common.actions.edit') : t('common.upload_file')}
          </button>
        )}
        {showDropzone && (
          <DropzoneDialog
            allowMultiple={false}
            dialogProps={{
              open: showDropzone,
            }}
            dropzoneOptions={{
              accept: getDropzoneAcceptOptionForAllDocumentTypes(),
            }}
            isUploading={isUploading}
            onCancel={toggleDropzone}
            onDone={async (files) => {
              setIsUploading(true)
              // Delete previously uploaded file, if not the original file
              await handleFileDelete()
              try {
                await promiseAll(
                  files.map((file) =>
                    createFileCellDocumentView({
                      variables: {
                        input: {
                          name: file.name,
                          file,
                        },
                      },
                    }),
                  ),
                )
                setIsUploading(false)
                toggleDropzone()
              } catch (err) {
                setIsUploading(false)
                snackbar.showError(`Failed to upload: ${err.message}`)
              }
            }}
          />
        )}
      </div>
    </InputWrapper>
  )
}
